import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useTopProducts } from "@/hooks/useTopProducts";
import ProductCard from "@/components/ProductCard";
import LoadingSpinner from "@/components/LoadingSpinner";
import { motion } from "framer-motion";
import { ShoppingBag, TrendingUp, Star, ArrowRight, Package, Sparkles, Crown } from "lucide-react";
import { getProductCount } from "@/services/productService";

const FeaturedProducts = () => {
  const { products: topProducts, loading, error } = useTopProducts({ limitCount: 4 });
  const [productCount, setProductCount] = useState<number | null>(null);

  // Fetch total product count
  useEffect(() => {
    const fetchProductCount = async () => {
      try {
        const count = await getProductCount('all');
        setProductCount(count);
      } catch (error) {
        setProductCount(null);
      }
    };

    fetchProductCount();
  }, []);

  // Enhanced Loading State
  if (loading) {
    return (
      <div className="relative bg-gradient-to-br from-white via-gray-50/30 to-white py-20 sm:py-24 overflow-hidden">
        {/* Animated Grid Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/5 via-transparent to-jamaica-yellow/5 opacity-30"></div>
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(jamaica-green/10 1px, transparent 1px), linear-gradient(90deg, jamaica-green/10 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="relative flex justify-center items-center min-h-[400px]">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="w-20 h-20 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-3xl flex items-center justify-center shadow-2xl mx-auto mb-6 animate-pulse">
              <LoadingSpinner size="md" />
            </div>
            <p className="text-lg font-semibold text-jamaica-green">Loading amazing products...</p>
          </motion.div>
        </div>
      </div>
    );
  }

  // Enhanced Error State
  if (error) {
    return (
      <div className="relative bg-gradient-to-br from-white via-gray-50/30 to-white py-20 sm:py-24 overflow-hidden">
        {/* Error Background Pattern */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-r from-red-50/50 via-transparent to-orange-50/50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Best Selling Products</h2>
            <p className="text-lg text-gray-600">Authentic Jamaican treats and treasures</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col items-center justify-center py-12"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 text-center max-w-md shadow-xl border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-white" />
              </div>
              <p className="text-gray-700 mb-4 font-medium">
                We're having trouble loading our featured products right now.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Please try refreshing the page or browse our full catalogue.
              </p>
              <Button className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white hover:from-jamaica-green/90 hover:to-jamaica-green shadow-lg hover:shadow-xl transition-all duration-300">
                <Link to="/products" className="flex items-center gap-2">
                  Browse All Products
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-gradient-to-br from-white via-gray-50/30 to-white py-20 sm:pt-24 overflow-hidden">
      {/* Unique Mesh/Grid Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(0, 116, 47, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 116, 47, 0.1) 1px, transparent 1px),
              linear-gradient(rgba(255, 201, 1, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 201, 1, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px, 60px 60px, 120px 120px, 120px 120px',
            backgroundPosition: '0 0, 0 0, 30px 30px, 30px 30px'
          }}></div>
        </div>
        
        {/* Triangle Decorative Elements */}
        <div className="absolute top-20 left-20 w-0 h-0 border-l-[20px] border-r-[20px] border-b-[35px] border-l-transparent border-r-transparent border-b-jamaica-green/10 animate-pulse delay-300"></div>
        <div className="absolute top-40 right-32 w-0 h-0 border-l-[15px] border-r-[15px] border-b-[26px] border-l-transparent border-r-transparent border-b-jamaica-yellow/15 animate-bounce delay-500"></div>
        <div className="absolute bottom-32 left-1/3 w-0 h-0 border-l-[25px] border-r-[25px] border-b-[43px] border-l-transparent border-r-transparent border-b-jamaica-green/8 animate-ping delay-700"></div>
        <div className="absolute bottom-20 right-20 w-0 h-0 border-l-[18px] border-r-[18px] border-b-[31px] border-l-transparent border-r-transparent border-b-jamaica-yellow/12 animate-pulse delay-900"></div>
        
        {/* Floating Geometric Shapes */}
        <div className="absolute top-1/4 right-10 w-12 h-12 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 transform rotate-45 animate-spin delay-1000" style={{ animationDuration: '15s' }}></div>
        <div className="absolute bottom-1/3 left-16 w-8 h-8 bg-gradient-to-br from-jamaica-yellow/15 to-jamaica-yellow/5 rounded-full animate-bounce delay-1200"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Enhanced Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-3 mb-6 sm:mb-4">
            <div className="relative">
              <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-jamaica-yellow to-amber-500 rounded-2xl flex items-center justify-center shadow-xl transform rotate-6 landing-featured-icon">
                <Crown className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-5 h-5 sm:w-6 sm:h-6 bg-jamaica-green rounded-full flex items-center justify-center animate-bounce">
                <TrendingUp className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
              </div>
            </div>
            <div className="bg-gradient-to-r from-jamaica-yellow/10 to-jamaica-green/10 px-3 py-1.5 sm:px-5 sm:py-2 rounded-2xl border border-jamaica-yellow/30 shadow-lg">
              <span className="text-xs sm:text-sm font-bold text-jamaica-black flex items-center gap-2">
                <Star className="w-4 h-4 text-jamaica-yellow fill-current" />
                Best Sellers
              </span>
            </div>
          </div>

          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-2 sm:mb-4">
            <span className="bg-gradient-to-r from-gray-900 via-jamaica-yellow to-gray-900 bg-clip-text text-transparent p-1">
              Our Best Selling Products
            </span>
          </h2>
          <p className="hidden sm:block text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Discover the most loved 
            <span className="text-jamaica-yellow font-semibold"> authentic Jamaican treats</span> and 
            <span className="text-jamaica-green font-semibold"> cultural treasures</span> that bring the island to your doorstep
          </p>
          <p className="sm:hidden text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
            Discover our most loved authentic Jamaican treats and cultural treasures.
          </p>
        </motion.div>
        
        {/* Enhanced Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10 mb-16 px-8 py-8">
          {topProducts.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 60, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ 
                duration: 0.8, 
                delay: index * 0.15,
                ease: "easeOut"
              }}
              viewport={{ once: true }}
              className="group relative sm:group"
            >
              {/* Product Card Container with Enhanced Styling */}
              <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl p-2 shadow-xl border-2 border-white/60 transform landing-product-card">
                
                {/* Animated Border Accent */}
                <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-500 rounded-t-3xl pointer-events-none"></div>
                
                {/* Product Card */}
                <div className="relative rounded-2xl overflow-hidden">
                  <ProductCard 
                    product={product} 
                    delay={0}
                    className="border-0 shadow-none bg-transparent"
                    variant="featured"
                  />
                </div>

                {/* Decorative Corner Triangles */}
                <div className="absolute bottom-4 right-4 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[14px] border-l-transparent border-r-transparent border-b-jamaica-yellow/30 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-500 delay-300 pointer-events-none"></div>
                <div className="absolute top-4 left-4 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[10px] border-l-transparent border-r-transparent border-b-jamaica-green/30 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-500 delay-400 pointer-events-none"></div>

                {/* Hover Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-jamaica-green/5 via-transparent to-jamaica-yellow/5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-500 rounded-3xl pointer-events-none"></div>
              </div>

              {/* Floating Rank Badge - Positioned outside the card */}
              <div className="absolute -top-6 -left-6 w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-xl text-white font-bold text-lg opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-500 transform rotate-0 sm:rotate-12 sm:group-hover:rotate-0 z-10 pointer-events-none">
                #{index + 1}
              </div>

              {/* Popularity Indicator - Positioned outside the card */}
              <div className="absolute -top-4 -right-4 bg-jamaica-yellow/90 backdrop-blur-sm px-3 py-1 rounded-full opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-500 delay-200 z-10 shadow-lg pointer-events-none">
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3 text-white" />
                  <span className="text-xs font-bold text-white">Popular</span>
                </div>
              </div>

              {/* External Floating Elements */}
              <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-jamaica-green/20 rounded-full opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-500 delay-100 animate-pulse z-10 pointer-events-none"></div>
              <div className="absolute -bottom-4 -right-4 w-8 h-8 bg-jamaica-yellow/20 rounded-full opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-500 delay-200 animate-bounce z-10 pointer-events-none"></div>
            </motion.div>
          ))}
        </div>
        
        {/* Enhanced Call-to-Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="inline-flex flex-col items-center gap-4">
            <Button 
              variant="outline" 
              className="h-14 border-2 border-jamaica-green bg-white/50 backdrop-blur-sm text-jamaica-green px-8 py-6 text-base font-accent shadow-lg rounded-xl group landing-cta-button"
            >
              <Link to="/products" className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5 group-hover:scale-110 transition-transform duration-300" />
                View All Available Products
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
            
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Sparkles className="w-4 h-4 text-jamaica-yellow animate-pulse" />
              <span>
                {productCount !== null 
                  ? `Over ${productCount.toLocaleString()} authentic products available`
                  : 'Authentic Jamaican products available'
                }
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FeaturedProducts;
